import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import ClearPathLogo from '../components/ClearPathLogo';
import LoadingScreen from '../components/LoadingScreen';
import { fmt, fmtK, fmtCents, fmtCount } from '../utils/formatters';

// Revenue-only by design: shared links never carry cost/profit/margin —
// that's the owner's negotiating position (v4 makes it invoice-verified).
// Orders only when every line has an order number, else items sold
// (backend order_metrics). A bare number is the pre-label API shape.
export function shareKpis(k: any): { label: string; value: string }[] {
  const tx = typeof k.transactions === 'number' ? { value: k.transactions } : k.transactions ?? {};
  const avg = typeof k.avg_order === 'number' ? { value: k.avg_order } : k.avg_order ?? {};
  // Counted per item, that card already is units sold — don't show it twice.
  const countsItems = tx.per === 'item';
  return [
    { label: 'Revenue', value: fmt(k.revenue) },
    { label: tx.label || 'Transactions', value: fmtCount(tx.value || 0) },
    ...(countsItems ? [] : [{ label: 'Units Sold', value: fmtCount(k.units || 0) }]),
    { label: avg.label || 'Avg Order', value: fmtCents(avg.value || 0) },
  ];
}

export default function SharePage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`/api/share/${token}`)
      .then((res) => setData(res.data))
      .catch((err) => {
        if (err.response?.status === 410) setError('This share link has expired.');
        else setError('Share link not found or invalid.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex flex-col items-center justify-center gap-4">
        <BarChart3 size={48} strokeWidth={1.5} className="text-[#4A2D5E]/30" />
        <h1 className="font-serif text-xl text-[#2D2A2E]">{error}</h1>
        <p className="font-sans text-sm text-[#6B6670]">Ask the business owner to share a new link.</p>
      </div>
    );
  }

  const kpis = shareKpis(data.kpis);

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      {/* Header */}
      <div className="bg-[#4A2D5E] px-6 py-4 flex items-center justify-between">
        <ClearPathLogo variant="white" size="sm" />
        <div className="text-right">
          <div className="font-serif text-white text-sm">{data.business_name}</div>
          <div className="font-sans text-white/60 text-xs">{data.period} Snapshot</div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="font-serif text-2xl text-[#2D2A2E]">{data.business_name}</h1>
          <p className="font-sans text-sm text-[#6B6670] mt-0.5">
            Shared performance snapshot · {data.period} · Generated {new Date(data.generated_at).toLocaleDateString()}
          </p>
        </div>

        {/* KPI Cards */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${kpis.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-4 mb-8`}>
          {kpis.map((k) => (
            <div key={k.label} className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-sm">
              <div className="text-xs font-sans font-medium text-[#6B6670] uppercase tracking-wide mb-1">{k.label}</div>
              <div className="text-xl font-sans font-bold text-[#2D2A2E] tabular-nums">{k.value}</div>
            </div>
          ))}
        </div>

        {/* Revenue Trend */}
        {data.trend && data.trend.length > 0 && (
          <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-sm mb-8">
            <h3 className="font-serif text-lg text-[#2D2A2E] mb-4">Revenue Trend</h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={data.trend}>
                <defs>
                  <linearGradient id="shareGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4A2D5E" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#4A2D5E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E2DE" />
                <XAxis dataKey="date" tick={{ fill: '#6B6670', fontSize: 11, fontFamily: 'DM Sans' }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: '#6B6670', fontSize: 11, fontFamily: 'DM Sans' }} tickFormatter={(v) => fmtK(v)} />
                <Tooltip
                  contentStyle={{ borderRadius: '10px', border: '1px solid #E6E2DE', fontFamily: 'DM Sans', fontSize: 12 }}
                  formatter={(value: number) => [fmt(value), 'Revenue']}
                  labelFormatter={(l) => `Date: ${l}`}
                />
                <Area type="monotone" dataKey="revenue" stroke="#4A2D5E" strokeWidth={2} fill="url(#shareGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top Products */}
        {data.top_products && data.top_products.length > 0 && (
          <div className="bg-white border border-[#E6E2DE] rounded-[14px] overflow-hidden shadow-sm">
            <div className="p-6 border-b border-[#E6E2DE]">
              <h3 className="font-serif text-lg text-[#2D2A2E]">Top Products</h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="bg-[#FAF9F7] border-b border-[#E6E2DE]">
                  {['#', 'Product', 'Revenue', 'Units'].map((h) => (
                    <th key={h} className={`p-4 text-xs font-medium text-[#6B6670] uppercase tracking-wide font-sans ${h === '#' || h === 'Product' ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.top_products.map((p: any, i: number) => (
                  <tr key={p.name} className="border-b border-[#E6E2DE] last:border-0">
                    <td className="p-4 text-sm font-sans text-[#6B6670] tabular-nums">{i + 1}</td>
                    <td className="p-4 text-sm font-sans text-[#2D2A2E] font-medium">{p.name}</td>
                    <td className="p-4 text-sm font-sans text-[#2D2A2E] text-right tabular-nums">{fmt(p.revenue)}</td>
                    <td className="p-4 text-sm font-sans text-[#2D2A2E] text-right tabular-nums">{fmtCount(p.units)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="font-sans text-xs text-[#6B6670] text-center mt-8">
          Powered by <span className="font-medium text-[#4A2D5E]">ClearPath</span> · Read-only snapshot · This link expires after 7 days
        </p>
      </div>
    </div>
  );
}
